import React, { useEffect, useState } from "react";
import { useAuth } from "./auth";
import { Link } from "react-router-dom";
import { Area, Event, InternationalRegisterationsMetaProps, ShiftWithDuty, Volunteer } from "../data/types";
import { CheckIcon, ChevronLeftIcon, ChevronRightIcon, XIcon } from "flowbite-react";
import { API_BASE_URL, LoadingSpinnerMeta, VolunteerFormModalMeta } from "../data";
import { Button, LoadingSpinner, VolunteerFormModal } from "../components";
import { DeleteIcon, DownloadIcon, EditIcon } from "../icons";
import { getShiftDisplayName, sortByName, sortShifts } from "../utils/sorting";

type Props = {
    meta: InternationalRegisterationsMetaProps
}

type PaginationData = {
    current_page: number
    last_page: number
    per_page: number
    total: number
}

type SortDirection = 'asc' | 'desc'

type InternationalRegistrationFilters = {
    name: string
    country: string
    family_origin: string
    jamat_khana: string
    age_start: string
    age_end: string
    age_missing: boolean
}

type TextFilterKey = Exclude<keyof InternationalRegistrationFilters, 'age_missing'>

const ITEMS_PER_PAGE = 10

const DEFAULT_INTERNATIONAL_FILTERS: InternationalRegistrationFilters = {
    name: '',
    country: '',
    family_origin: '',
    jamat_khana: '',
    age_start: '',
    age_end: '',
    age_missing: false
}

const normalizeApiList = <T,>(result: any): T[] => {
    let data = result.value || result

    if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
        data = data[0]
    }

    return Array.isArray(data) ? data : []
}

const normalizeInternationalVolunteer = (volunteer: any): Volunteer => ({
    ...volunteer,
    eligible_for_duty: volunteer.eligible_for_duty === 1,
    ismaili: volunteer.ismaili === 1,
    registered_volunteer: volunteer.registered_volunteer === 1,
    multi_faith_family: volunteer.multi_faith_family === 1,
    approved_volunteer: volunteer.approved_volunteer === null || volunteer.approved_volunteer === undefined
        ? null
        : volunteer.approved_volunteer === true || volunteer.approved_volunteer === 1,
    volunteer_for_visit: volunteer.volunteer_for_visit !== null && volunteer.volunteer_for_visit !== undefined ? Number(volunteer.volunteer_for_visit) : volunteer.volunteer_for_visit
})

const formatLastSignInDate = (dateStr: string | null | undefined, fallback: string) => {
    if (!dateStr) return fallback

    const date = new Date(dateStr)
    if (Number.isNaN(date.getTime())) return fallback

    return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    })
}

export const InternationalRegisterations = (props: Props) => {
    const { meta } = props;
    const { user } = useAuth()
    const [loadingPage, setLoadingPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [volunteers, setVolunteers] = useState<Volunteer[]>([]);
    const [selectedVolunteer, setSelectedVolunteer] = useState<Volunteer | null>(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [events, setEvents] = useState<Event[]>([])
    const [areas, setAreas] = useState<Area[]>([])
    const [shifts, setShifts] = useState<ShiftWithDuty[]>([])
    const [selectedEventId, setSelectedEventId] = useState<number | null>(null)
    const [selectedAreaId, setSelectedAreaId] = useState<number | null>(null)
    const [selectedShiftId, setSelectedShiftId] = useState<number | null>(null)

    const [filters, setFilters] = useState<InternationalRegistrationFilters>(DEFAULT_INTERNATIONAL_FILTERS);
    const [statusFilter, setStatusFilter] = useState<'all' | 'approved' | 'pending'>('all');
    const [lastSignInSortDirection, setLastSignInSortDirection] = useState<SortDirection | null>(null)

    const canApprove = user.permissions?.includes('approve_core_team')
    const canManage = user.permissions?.includes('manage_core_team')

    const [pagination, setPagination] = useState<PaginationData>({
        current_page: 1,
        last_page: 1,
        per_page: ITEMS_PER_PAGE,
        total: 0
    });

    // 2. UPDATE QUERY BUILDER
    const buildQueryString = (page: number) => {
        const params = new URLSearchParams()
        params.append('per_page', ITEMS_PER_PAGE.toString());
        params.append('page', page.toString());
        params.append('international_volunteer', "1");

        if (filters.name) params.append('name', filters.name);
        if (filters.country) params.append('country_of_birth', filters.country);
        if (filters.family_origin) params.append('family_origin', filters.family_origin);
        if (filters.jamat_khana) params.append('jamat_khana', filters.jamat_khana);
        if (filters.age_missing) {
            params.append('age_missing', '1');
        } else {
            if (filters.age_start) params.append('age_start', filters.age_start);
            if (filters.age_end) params.append('age_end', filters.age_end);
        }

        if (statusFilter === 'all') {
            params.append('approved_volunteer', 'not_null');
        } else {
            params.append('approved_volunteer', statusFilter === 'approved' ? '1' : '0');
        }

        if (lastSignInSortDirection) {
            params.append('sort_by', 'last_sign_in_at')
            params.append('sort_dir', lastSignInSortDirection)
        }

        return params.toString()
    }

    const fetchInternationalVolunteers = async (page: number) => {
        setLoadingPage(page)
        setLoading(true)
        setError(null)

        try {
            const queryString = buildQueryString(page)
            // console.log("Query:", queryString);
            const response = await fetch(`${API_BASE_URL}/volunteer?${queryString}`, { credentials: 'include' })
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }
            const result = await response.json()

            const volunteersData = result.data || []

            const normalized = volunteersData.map(normalizeInternationalVolunteer)

            setVolunteers(normalized)
            setPagination({
                current_page: result.current_page || 1,
                last_page: result.last_page || 1,
                per_page: result.per_page || ITEMS_PER_PAGE,
                total: result.total || 0
            })
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred while fetching volunteers')
        } finally {
            setLoading(false)
        }
    }

    // Effect for status filter change
    useEffect(() => {
        fetchInternationalVolunteers(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, lastSignInSortDirection]);

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/event`, { credentials: 'include' })
                if (!response.ok) return

                const result = await response.json()
                setEvents(sortByName(normalizeApiList<Event>(result)))
            } catch (err) {
                console.error('Error fetching events:', err)
            }
        }

        fetchEvents()
    }, [])

    useEffect(() => {
        if (!selectedEventId) {
            setAreas([])
            setSelectedAreaId(null)
            return
        }

        const fetchAreas = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/area/${selectedEventId}`, { credentials: 'include' })
                if (!response.ok) return

                const result = await response.json()
                setAreas(sortByName(normalizeApiList<Area>(result)))
            } catch (err) {
                console.error('Error fetching areas:', err)
            }
        }

        fetchAreas()
        setSelectedAreaId(null)
        setSelectedShiftId(null)
        setShifts([])
    }, [selectedEventId])

    useEffect(() => {
        if (!selectedAreaId) {
            setShifts([])
            setSelectedShiftId(null)
            return
        }

        const fetchShifts = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/shift/${selectedAreaId}`, { credentials: 'include' })
                if (!response.ok) return

                const result = await response.json()
                setShifts(sortShifts(normalizeApiList<ShiftWithDuty>(result)))
            } catch (err) {
                console.error('Error fetching shifts:', err)
            }
        }

        fetchShifts()
        setSelectedShiftId(null)
    }, [selectedAreaId])

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        fetchInternationalVolunteers(1);
    }

    const handleFilterChange = (field: TextFilterKey, value: string) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    }

    const handleAgeMissingChange = (checked: boolean) => {
        setFilters(prev => ({
            ...prev,
            age_start: checked ? '' : prev.age_start,
            age_end: checked ? '' : prev.age_end,
            age_missing: checked
        }));
    }

    const handleLastSignInSort = () => {
        setLastSignInSortDirection(prev => prev === 'desc' ? 'asc' : 'desc')
    }

    const renderLastSignInSortIcon = () => {
        if (!lastSignInSortDirection) return <span className="text-gray-400">⇅</span>
        return <span>{lastSignInSortDirection === 'asc' ? '↑' : '↓'}</span>
    }

    const handleClearFilters = async () => {
        setFilters(DEFAULT_INTERNATIONAL_FILTERS);
        setStatusFilter('all');
        setLastSignInSortDirection(null);

        // Manually fetch clean data
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('per_page', ITEMS_PER_PAGE.toString());
            params.append('page', '1');
            params.append('international_volunteer', "1");
            params.append('approved_volunteer', 'not_null');

            const response = await fetch(`${API_BASE_URL}/volunteer?${params.toString()}`, { credentials: 'include' });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const result = await response.json();
            const volunteersData = result.data || [];

            const normalized = volunteersData.map(normalizeInternationalVolunteer)
            setVolunteers(normalized);
            setPagination({
                current_page: result.current_page || 1,
                last_page: result.last_page || 1,
                per_page: result.per_page || ITEMS_PER_PAGE,
                total: result.total || 0
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred while fetching volunteers');
        } finally {
            setLoading(false);
        }
    }

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= pagination.last_page) {
            fetchInternationalVolunteers(page)
        }
    }

    // ... (rest of approval/edit handlers) ...
    const handleApprovalClick = async (volunteer: Volunteer, approval: boolean | null) => {
        if (!volunteer?.csid) return
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/volunteer/${volunteer.csid}/approve`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    'approved_status': approval,
                    ...(approval === true && selectedAreaId && selectedShiftId ? {
                        area_id: selectedAreaId,
                        shift_id: selectedShiftId
                    } : {})
                })
            })
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `Failed to update volunteer: ${response.status}`)
            }
            fetchInternationalVolunteers(pagination.current_page);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Error while updating volunteer approval.");
            setLoading(false);
        }
    }

    const handleEditVolunteerClick = (volunteer: Volunteer) => {
        setSelectedVolunteer(volunteer);
        setIsEditModalOpen(true);
    }

    const handleEditVolunteerClickClose = () => {
        setSelectedVolunteer(null);
        setIsEditModalOpen(false);
    }

    const handleEditVolunteer = async (volunteerData: any) => {
        if (!selectedVolunteer?.csid) return
        try {
            const { id, csid, created_at, updated_at, age, ...updateData } = volunteerData
            const response = await fetch(`${API_BASE_URL}/volunteer/${selectedVolunteer.csid}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(updateData)
            })

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `Failed to update volunteer: ${response.status}`)
            }
            fetchInternationalVolunteers(pagination.current_page);
        } catch (err) {
            console.error('Error updating volunteer:', err)
            throw err
        }
    }

    // CSV Export function - fetches ALL data matching current filters using lightweight endpoint
    const handleExportCSV = async () => {
        setExporting(true)
        try {
            // Build query params for export endpoint (no pagination needed)
            const params = new URLSearchParams()
            params.append('international_volunteer', '1')

            if (filters.name) params.append('name', filters.name)
            if (filters.country) params.append('country_of_birth', filters.country)
            if (filters.family_origin) params.append('family_origin', filters.family_origin)
            if (filters.jamat_khana) params.append('jamat_khana', filters.jamat_khana)
            if (filters.age_missing) {
                params.append('age_missing', '1')
            } else {
                if (filters.age_start) params.append('age_start', filters.age_start)
                if (filters.age_end) params.append('age_end', filters.age_end)
            }

            if (statusFilter !== 'all') {
                params.append('approved_volunteer', statusFilter === 'approved' ? '1' : '0')
            } else {
                params.append('approved_volunteer', 'not_null')
            }

            if (lastSignInSortDirection) {
                params.append('sort_by', 'last_sign_in_at')
                params.append('sort_dir', lastSignInSortDirection)
            }

            // Use the lightweight export endpoint
            const response = await fetch(`${API_BASE_URL}/volunteer/export?${params.toString()}`, { credentials: 'include' })
            if (!response.ok) {
                throw new Error(`Failed to fetch data for export: ${response.status}`)
            }
            // Export endpoint returns array directly (not paginated)
            const dataToExport = await response.json()

            // Define CSV headers
            const headers = [
                'ID',
                'First Name',
                'Last Name',
                'Email',
                'Phone',
                'Age',
                'Country',
                'Family Origin',
                'Jamatkhana',
                'Ismaili',
                'Inter-Faith Family',
                meta.lastSignInColumn,
                'Approval Status'
            ]

            // Convert data to CSV rows
            const rows = dataToExport.map((item: any) => [
                item.csid ? `="${item.csid}"` : '',
                item.first_name || '',
                item.last_name || '',
                item.email || '',
                item.phone ? `="${item.phone}"` : '',
                item.age?.toString() || '',
                item.country || '',
                item.family_origin || '',
                item.jamat_khana || '',
                item.ismaili === 1 ? 'Yes' : 'No',
                item.multi_faith_family === 1 ? 'Yes' : 'No',
                formatLastSignInDate(item.user?.last_sign_in_at, meta.neverSignedInText),
                item.approved_volunteer === null ? 'Removed' : item.approved_volunteer ? 'Approved' : 'Pending'
            ])

            // Combine headers and rows
            const csvRows = [headers]
            rows.forEach((row: string[]) => {
                const processedRow = row.map(cell => {
                    let cellStr = String(cell)
                    // Replace newlines and carriage returns with spaces
                    cellStr = cellStr.replace(/[\r\n]+/g, ' ').trim()
                    // Don't escape Excel formulas (cells starting with =)
                    if (cellStr.startsWith('=')) {
                        return cellStr
                    }
                    // Escape cells containing commas or quotes
                    if (cellStr.includes(',') || cellStr.includes('"')) {
                        return `"${cellStr.replace(/"/g, '""')}"`
                    }
                    return cellStr
                })
                csvRows.push(processedRow)
            })

            const csvContent = csvRows.map(row => row.join(',')).join('\n')

            // Create blob and download
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
            const link = document.createElement('a')
            const url = URL.createObjectURL(blob)

            // Generate filename with current date
            const date = new Date().toISOString().split('T')[0]
            link.setAttribute('href', url)
            link.setAttribute('download', `international_registrations_${date}.csv`)
            link.style.visibility = 'hidden'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        } catch (err) {
            console.error('Error exporting CSV:', err)
            setError(err instanceof Error ? err.message : 'Failed to export data')
        } finally {
            setExporting(false)
        }
    }

    return (
        <>
            <div className="flex-1 pt-8 pb-8">
                <div className="container mx-auto px-4">
                    <div className='mb-4'>
                        <Link
                            to="/admin"
                            className="inline-flex items-center text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                        >
                            <ChevronLeftIcon className="w-5 h-5 me-2" />
                            Back to Admin Hub
                        </Link>
                    </div>
                    <div className="text-center mb-8">
                        <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                            {meta.title}
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 text-lg mb-6">
                            {meta.description}
                        </p>
                    </div>

                    {canApprove && (
                        <div className="mb-6 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                            <div className="mb-3">
                                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Auto allocation</h2>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Choose a shift to assign volunteers to when approving them.</p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label htmlFor="allocation-event" className="block mb-1 text-sm font-medium text-gray-900 dark:text-white">Event</label>
                                    <select
                                        id="allocation-event"
                                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                        value={selectedEventId ?? ''}
                                        onChange={(e) => setSelectedEventId(e.target.value ? Number(e.target.value) : null)}
                                    >
                                        <option value="">No auto allocation</option>
                                        {events.map((event) => (
                                            <option key={event.id} value={event.id}>{event.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="allocation-area" className="block mb-1 text-sm font-medium text-gray-900 dark:text-white">Area</label>
                                    <select
                                        id="allocation-area"
                                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:opacity-60"
                                        value={selectedAreaId ?? ''}
                                        onChange={(e) => setSelectedAreaId(e.target.value ? Number(e.target.value) : null)}
                                        disabled={!selectedEventId}
                                    >
                                        <option value="">Select area</option>
                                        {areas.map((area) => (
                                            <option key={area.id} value={area.id}>{area.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="allocation-shift" className="block mb-1 text-sm font-medium text-gray-900 dark:text-white">Shift</label>
                                    <select
                                        id="allocation-shift"
                                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:opacity-60"
                                        value={selectedShiftId ?? ''}
                                        onChange={(e) => setSelectedShiftId(e.target.value ? Number(e.target.value) : null)}
                                        disabled={!selectedAreaId}
                                    >
                                        <option value="">Select shift</option>
                                        {shifts.map((shift) => (
                                            <option key={shift.id} value={shift.id}>
                                                {getShiftDisplayName(shift)} ({shift.start_time?.slice(0, 5)}-{shift.end_time?.slice(0, 5)})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* 3. UPDATED FILTER UI */}
                    <div className="mb-6 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                        <form onSubmit={handleSearchSubmit} className="flex flex-col gap-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                {/* Name Input */}
                                <div>
                                    <label htmlFor="name-search" className="block mb-1 text-sm font-medium text-gray-900 dark:text-white">Name</label>
                                    <input
                                        id="name-search"
                                        type="text"
                                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                                        placeholder="Search Name..."
                                        value={filters.name}
                                        onChange={(e) => handleFilterChange('name', e.target.value)}
                                    />
                                </div>

                                {/* Country Input */}
                                <div>
                                    <label htmlFor="country-search" className="block mb-1 text-sm font-medium text-gray-900 dark:text-white">Country</label>
                                    <input
                                        id="country-search"
                                        type="text"
                                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                                        placeholder="Search Country..."
                                        value={filters.country}
                                        onChange={(e) => handleFilterChange('country', e.target.value)}
                                    />
                                </div>

                                {/* Family Origin Select */}
                                <div>
                                    <label htmlFor="origin-search" className="block mb-1 text-sm font-medium text-gray-900 dark:text-white">Family Origin</label>
                                    <select
                                        id="origin-search"
                                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                                        value={filters.family_origin}
                                        onChange={(e) => handleFilterChange('family_origin', e.target.value)}
                                    >
                                        <option value="">All Origins</option>
                                        <option value="East Africa">East Africa</option>
                                        <option value="Indian Subcontinent">Indian Subcontinent</option>
                                        <option value="Afghanistan">Afghanistan</option>
                                        <option value="Tajikistan">Tajikistan</option>
                                        <option value="Syria">Syria</option>
                                        <option value="Iran">Iran</option>
                                        <option value="Mixed Origin">Mixed Origin</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>

                                {/* Jamatkhana Input */}
                                <div>
                                    <label htmlFor="jk-search" className="block mb-1 text-sm font-medium text-gray-900 dark:text-white">Jamatkhana</label>
                                    <input
                                        id="jk-search"
                                        type="text"
                                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                                        placeholder="Search Jamatkhana..."
                                        value={filters.jamat_khana}
                                        onChange={(e) => handleFilterChange('jamat_khana', e.target.value)}
                                    />
                                </div>

                                <div className="sm:col-span-2 lg:col-span-2">
                                    <label className="block mb-1 text-sm font-medium text-gray-900 dark:text-white">Age</label>
                                    <div className="flex flex-col sm:flex-row gap-3">
                                        <div className="flex items-center gap-2 flex-1">
                                            <input
                                                id="age-start-search"
                                                type="number"
                                                min="0"
                                                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white disabled:opacity-60"
                                                placeholder="Min"
                                                value={filters.age_start}
                                                onChange={(e) => handleFilterChange('age_start', e.target.value)}
                                                disabled={filters.age_missing}
                                            />
                                            <span className="text-sm text-gray-500 dark:text-gray-400">to</span>
                                            <input
                                                id="age-end-search"
                                                type="number"
                                                min="0"
                                                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white disabled:opacity-60"
                                                placeholder="Max"
                                                value={filters.age_end}
                                                onChange={(e) => handleFilterChange('age_end', e.target.value)}
                                                disabled={filters.age_missing}
                                            />
                                        </div>
                                        <label htmlFor="age-missing-search" className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                            <input
                                                id="age-missing-search"
                                                type="checkbox"
                                                className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 dark:focus:ring-primary-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                                checked={filters.age_missing}
                                                onChange={(e) => handleAgeMissingChange(e.target.checked)}
                                            />
                                            No age recorded
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-col md:flex-row justify-between items-center gap-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                                {/* Status Filter Buttons */}
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</span>
                                    <div className="inline-flex rounded-md shadow-sm" role="group">
                                        <button
                                            type="button"
                                            onClick={() => setStatusFilter('all')}
                                            className={`px-4 py-2 text-sm font-medium border border-gray-200 rounded-s-lg hover:bg-gray-100 hover:text-primary-700 focus:z-10 focus:ring-2 focus:ring-primary-700 focus:text-primary-700 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${statusFilter === 'all' ? 'bg-gray-100 text-primary-700 dark:bg-gray-600' : 'bg-white'}`}
                                        >
                                            All
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setStatusFilter('approved')}
                                            className={`px-4 py-2 text-sm font-medium border-t border-b border-e border-gray-200 hover:bg-gray-100 hover:text-primary-700 focus:z-10 focus:ring-2 focus:ring-primary-700 focus:text-primary-700 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${statusFilter === 'approved' ? 'bg-gray-100 text-primary-700 dark:bg-gray-600' : 'bg-white'}`}
                                        >
                                            Approved
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setStatusFilter('pending')}
                                            className={`px-4 py-2 text-sm font-medium border border-gray-200 rounded-e-lg hover:bg-gray-100 hover:text-primary-700 focus:z-10 focus:ring-2 focus:ring-primary-700 focus:text-primary-700 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${statusFilter === 'pending' ? 'bg-gray-100 text-primary-700 dark:bg-gray-600' : 'bg-white'}`}
                                        >
                                            Pending
                                        </button>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex gap-2 w-full md:w-auto">
                                    <Button
                                        type="button"
                                        size="md"
                                        variant="outline"
                                        onClick={handleClearFilters}
                                        className="border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                                    >
                                        Clear Filters
                                    </Button>
                                    <Button type="submit" size="md">
                                        Apply Filters
                                    </Button>
                                    <Button
                                        type="button"
                                        size="md"
                                        variant="outline"
                                        onClick={handleExportCSV}
                                        disabled={volunteers.length === 0 || exporting}
                                        className="border-primary-300 text-primary-700 hover:bg-primary-50 dark:border-primary-600 dark:text-primary-300 dark:hover:bg-primary-900"
                                    >
                                        {exporting ? (
                                            <>
                                                <svg className="animate-spin w-4 h-4 me-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Exporting...
                                            </>
                                        ) : (
                                            <>
                                                <DownloadIcon className="w-4 h-4 me-2" />
                                                {meta.exportButton}
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </form>
                    </div>

                    {/* ... (Rest of component: Loading, Error, Table, Pagination) ... */}
                    {/* (This part remains exactly the same as previous) */}
                    {loading && (
                        <div className="flex justify-center items-center min-h-[400px]">
                            <LoadingSpinner meta={LoadingSpinnerMeta} />
                        </div>
                    )}

                    {!loading && error && (
                        <div className="max-w-2xl mx-auto mb-6">
                            <div className="bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-6 py-4 rounded-lg">
                                <p className="font-bold text-lg mb-2">{meta.errorTitle}</p>
                                <p>{error}</p>
                            </div>
                        </div>
                    )}

                    {!loading && (
                        <div className="hidden md:block overflow-x-auto">
                            {volunteers.length > 0 ? (
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead className="bg-gray-50 dark:bg-gray-900">
                                        <tr>
                                            <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                                            <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email</th>
                                            <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Phone</th>
                                            <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Age</th>
                                            <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Country</th>
                                            <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Family Origin</th>
                                            <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Jamatkhana</th>
                                            <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ismaili</th>
                                            <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Inter-Faith</th>
                                            <th
                                                aria-sort={lastSignInSortDirection ? (lastSignInSortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
                                                className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                                            >
                                                <button
                                                    type="button"
                                                    onClick={handleLastSignInSort}
                                                    className="inline-flex items-center gap-1 uppercase"
                                                >
                                                    {meta.lastSignInColumn} {renderLastSignInSortIcon()}
                                                </button>
                                            </th>
                                            {(canApprove || canManage) && (
                                                <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                                            )}
                                            <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                        {volunteers.map((item) => (
                                            <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                                                    {item.first_name} {item.last_name}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{item.email}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{item.phone}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{item.age}</td>
                                                <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">{item.country}</td>
                                                <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">{item.family_origin}</td>
                                                <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">{item.jamat_khana}</td>
                                                <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">{item.ismaili ? "Yes" : "No"}</td>
                                                <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">{item.multi_faith_family ? "Yes" : "No"}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                    {formatLastSignInDate(item.user?.last_sign_in_at, meta.neverSignedInText)}
                                                </td>
                                                {(canApprove || canManage) && (
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                        <div className="flex gap-2">
                                                            {canApprove && item.approved_volunteer === false && (
                                                                <>
                                                                    <button
                                                                        onClick={() => { handleApprovalClick(item, true) }}
                                                                        className="p-2 bg-success-600 text-white rounded hover:bg-success-700 transition-colors"
                                                                        title="Approve"
                                                                    >
                                                                        <CheckIcon className="w-4 h-4" />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => { handleApprovalClick(item, null) }}
                                                                        className="p-2 bg-danger-600 text-white rounded hover:bg-danger-700 transition-colors"
                                                                        title="Remove request"
                                                                    >
                                                                        <DeleteIcon className="w-4 h-4" />
                                                                    </button>
                                                                </>
                                                            )}
                                                            {item.approved_volunteer === true && (
                                                                <button
                                                                    onClick={() => { handleApprovalClick(item, false) }}
                                                                    className="p-2 bg-warn-600 text-white rounded hover:bg-warn-700 transition-colors"
                                                                    title="Unapprove"
                                                                >
                                                                    <XIcon className="w-4 h-4" />
                                                                </button>
                                                            )}
                                                            <button
                                                                onClick={() => handleEditVolunteerClick(item)}
                                                                className="p-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors"
                                                                title="Edit"
                                                            >
                                                                <EditIcon className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                )}
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${item.approved_volunteer === null
                                                        ? 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                                                        : item.approved_volunteer
                                                            ? 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200'
                                                            : 'bg-warn-100 text-warn-800 dark:bg-warn-900 dark:text-warn-200'
                                                        }`}>
                                                        {item.approved_volunteer === null ? "Removed" : item.approved_volunteer ? "Approved" : "Pending"}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div className="text-center text-gray-500 dark:text-gray-400 py-12">
                                    No International Registrations found matching the filters.
                                </div>
                            )}
                        </div>
                    )}

                    {/* Pagination Controls */}
                    {pagination.last_page > 1 && (
                        <div className="flex items-center justify-between mt-4">
                            <div className="flex items-center gap-2">
                                <Button type="button" onClick={() => handlePageChange(1)} disabled={pagination.current_page <= 1} size="sm" variant="icon">
                                    <ChevronLeftIcon /><ChevronLeftIcon className="w-5 h-5 -ms-[22px]" /> First
                                </Button>
                                <Button type="button" onClick={() => handlePageChange(pagination.current_page - 1)} disabled={pagination.current_page <= 1} size="sm">
                                    <ChevronLeftIcon /> Previous
                                </Button>
                                <span className="px-2 text-sm text-gray-600 dark:text-gray-400">
                                    Page {pagination.current_page} of {pagination.last_page}
                                </span>
                                <Button type="button" onClick={() => handlePageChange(pagination.current_page + 1)} disabled={pagination.current_page >= pagination.last_page} size="sm">
                                    Next <ChevronRightIcon />
                                </Button>
                                <Button type="button" onClick={() => handlePageChange(pagination.last_page)} disabled={pagination.current_page >= pagination.last_page} size="sm" variant="icon">
                                    Last <ChevronRightIcon /><ChevronRightIcon className="w-5 h-5 -ms-[22px]" />
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <VolunteerFormModal
                isOpen={isEditModalOpen}
                onClose={handleEditVolunteerClickClose}
                onSubmit={handleEditVolunteer}
                initialVolunteer={selectedVolunteer}
                meta={VolunteerFormModalMeta}
                adminSide={true}
            />
        </>
    )
}
